/**
 * API service — calls the SWA Azure Functions backend at /api/*.
 */

const API_BASE = "/api";

// Clerk token getter — set once from React tree via setAuthTokenGetter()
let _getToken = null;

// Tenant ID — set once during bootstrap via setApiTenantId()
let _tenantId = null;

/**
 * Called from TenantBootstrap to set the tenant ID for all API requests.
 * The backend cross-checks this against hostname-derived tenant.
 */
export function setApiTenantId(id) {
  _tenantId = id;
}

/**
 * Called from the app root to provide Clerk's getToken function.
 * This lets apiFetch attach the Bearer token to every API request.
 */
export function setAuthTokenGetter(fn) {
  _getToken = fn;
}

/**
 * Resilient fetch wrapper — retries once on network errors (cold starts, timeouts)
 * and converts raw "Failed to fetch" into a user-friendly message.
 * Automatically attaches Clerk auth token when available.
 */
export async function apiFetch(url, options = {}, { retries = 1 } = {}) {
  // Attach tenant ID header for backend cross-check
  if (_tenantId) {
    options.headers = { ...options.headers, "X-Tenant-Id": _tenantId };
  }

  // Attach Clerk session token as Bearer header
  if (_getToken) {
    try {
      const token = await _getToken();
      if (token) {
        options.headers = { ...options.headers, "X-Clerk-Token": token };
      }
    } catch { /* proceed without token */ }
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetch(url, options);
    } catch (err) {
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 1500));
        continue;
      }
      throw new Error("Unable to reach the server. Please check your connection and try again.");
    }
  }
}

// ── Contacts ──────────────────────────────────────────────────

export async function fetchContacts({
  search = "",
  tag = "",
  limit = 100,
  offset = 0,
  sort = "",
  reviewStatus = "",
  includeArchived = false,
} = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (tag) params.set("tag", tag);
  if (sort) params.set("sort", sort);
  if (reviewStatus) params.set("reviewStatus", reviewStatus);
  if (includeArchived) params.set("includeArchived", "true");
  params.set("limit", limit);
  params.set("offset", offset);

  const res = await apiFetch(`${API_BASE}/contacts?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch contacts: ${res.status}`);
  return res.json(); // { contacts: [], total: number }
}

export async function fetchContact(id, email) {
  const params = email ? `?email=${encodeURIComponent(email)}` : "";
  const res = await apiFetch(`${API_BASE}/contacts/${id}${params}`);
  if (!res.ok) throw new Error(`Failed to fetch contact: ${res.status}`);
  return res.json();
}

export async function createContact(contact) {
  const res = await apiFetch(`${API_BASE}/contacts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contact),
  });
  if (!res.ok) throw new Error(`Failed to create contact: ${res.status}`);
  return res.json();
}

export async function updateContact(id, updates) {
  const res = await apiFetch(`${API_BASE}/contacts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Failed to update contact: ${res.status}`);
  return res.json();
}

export async function sendWelcomeEmail(id, email) {
  const res = await apiFetch(`${API_BASE}/send-welcome/${id}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`Failed to send welcome email: ${res.status}`);
  return res.json();
}

export async function deleteContact(id, email) {
  const params = email ? `?email=${encodeURIComponent(email)}` : "";
  const res = await apiFetch(`${API_BASE}/contacts/${id}${params}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete contact: ${res.status}`);
}

export async function bulkUpdateContacts(contactIds, updates, addTags = [], removeTags = []) {
  const res = await apiFetch(`${API_BASE}/contacts/bulk-update`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contactIds, updates, addTags, removeTags }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Bulk update failed: ${res.status}`);
  }
  return res.json();
}

// ── Companies ────────────────────────────────────────────────

export async function fetchCompanies({ search = "" } = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const res = await apiFetch(`${API_BASE}/companies?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch companies: ${res.status}`);
  return res.json(); // { companies: [{ id, name, contacts: [], ... }], total }
}

export async function updateCompany(id, updates) {
  const res = await apiFetch(`${API_BASE}/companies/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update company: ${res.status}`);
  }
  return res.json();
}

export async function mergeCompanies(sourceId, targetId) {
  const res = await apiFetch(`${API_BASE}/companies/merge`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceId, targetId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to merge companies: ${res.status}`);
  }
  return res.json();
}

export async function mergeContacts(sourceId, targetId) {
  const res = await apiFetch(`${API_BASE}/contacts/merge`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceId, targetId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to merge contacts: ${res.status}`);
  }
  return res.json();
}

// ── Tags ─────────────────────────────────────────────────────

export async function fetchTags() {
  const res = await apiFetch(`${API_BASE}/contacts/tags`);
  if (!res.ok) throw new Error(`Failed to fetch tags: ${res.status}`);
  return res.json(); // { tags: [{ tag, count }], total }
}

// ── Sync status ───────────────────────────────────────────────

export async function fetchSyncStatus() {
  const res = await apiFetch(`${API_BASE}/ghl-sync/status`);
  if (!res.ok) throw new Error(`Failed to fetch sync status: ${res.status}`);
  return res.json();
}

export async function triggerFullSync() {
  const res = await apiFetch(`${API_BASE}/ghl-sync/full`, { method: "POST" });
  if (!res.ok) throw new Error(`Sync failed: ${res.status}`);
  return res.json();
}

// ── Pipeline Move ────────────────────────────────────────────

export async function movePipelineStage(contactId, email, toStage) {
  const res = await apiFetch(`${API_BASE}/pipeline/move`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contactId, email, toStage }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to move contact: ${res.status}`);
  }
  return res.json();
}

// ── Engagement ────────────────────────────────────────────────

// ── Newsletters (GHL campaigns) ───────────────────────────────

export async function fetchNewsletters({ status, includeHidden } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (includeHidden) params.set("includeHidden", "true");
  const res = await apiFetch(`${API_BASE}/campaigns?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch newsletters: ${res.status}`);
  return res.json(); // { campaigns, total, stats, statusCounts }
}

export async function fetchWebcastCampaigns() {
  const res = await apiFetch(
    `${API_BASE}/campaigns?search=webinar,webcast&status=completed`,
  );
  if (!res.ok)
    throw new Error(`Failed to fetch webcast campaigns: ${res.status}`);
  return res.json(); // { campaigns, total }
}

// ── Newsletter Drafts (Insights) ───────────────────────────────

export async function fetchNewsletterDrafts({ status, includeHidden } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (includeHidden) params.set("includeHidden", "true");
  const res = await apiFetch(`${API_BASE}/newsletters?${params}`);
  if (!res.ok)
    throw new Error(`Failed to fetch newsletter drafts: ${res.status}`);
  return res.json(); // { newsletters, total }
}

export async function fetchNewsletterDraft(id) {
  const res = await apiFetch(`${API_BASE}/newsletters/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch newsletter: ${res.status}`);
  return res.json();
}

export async function saveNewsletterDraft(draft) {
  const res = await apiFetch(`${API_BASE}/newsletters`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(draft),
  });
  if (!res.ok)
    throw new Error(`Failed to save newsletter draft: ${res.status}`);
  return res.json();
}

export async function deleteNewsletterDraft(id) {
  const res = await apiFetch(`${API_BASE}/newsletters/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete newsletter: ${res.status}`);
}

export async function sendNewsletter(
  id,
  { sendMethod, senderEmail, graphToken } = {},
) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/newsletters/${id}/send`, {
    method: "POST",
    headers,
    body: JSON.stringify({ sendMethod, senderEmail }),
  });
  if (!res.ok) {
    let details = "";
    try {
      const body = await res.json();
      details = body.error || "";
    } catch {}
    throw new Error(
      `Failed to send newsletter: ${res.status}${details ? " — " + details : ""}`,
    );
  }
  return res.json(); // { total, sent, failed, skipped, errors }
}

export async function resendToUnopened(id, { sendMethod, senderEmail, graphToken, subjectPrefix } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/newsletters/${id}/resend-unopened`, {
    method: "POST",
    headers,
    body: JSON.stringify({ sendMethod, senderEmail, subjectPrefix }),
  });
  if (!res.ok) {
    let details = "";
    try {
      const body = await res.json();
      details = body.error || "";
    } catch {}
    throw new Error(details || `Resend failed: ${res.status}`);
  }
  return res.json();
}

export async function sendTrackedEmail({ to, subject, bodyHtml, sendMethod, senderEmail, graphToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/email-send`, {
    method: "POST",
    headers,
    body: JSON.stringify({ to, subject, bodyHtml, sendMethod, senderEmail }),
  });
  if (!res.ok) {
    let details = "";
    try { const body = await res.json(); details = body.error || ""; } catch {}
    throw new Error(details || `Email send failed: ${res.status}`);
  }
  return res.json();
}

export async function testSendNewsletter({
  subject,
  mjmlTemplate,
  bodyHtml,
  sendMethod,
  senderEmail,
  graphToken,
} = {}) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/newsletters/test-send`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      subject,
      mjmlTemplate,
      bodyHtml,
      sendMethod,
      senderEmail,
    }),
  });
  if (!res.ok) {
    let details = "";
    try {
      const body = await res.json();
      details = body.error || "";
    } catch {}
    throw new Error(
      `Test send failed: ${res.status}${details ? " — " + details : ""}`,
    );
  }
  return res.json(); // { success, sentTo, sendMethod }
}

// ── Newsletter Analytics ──────────────────────────────────────

export async function fetchNewsletterAnalytics(id) {
  const res = await apiFetch(`${API_BASE}/newsletters/${id}/analytics`);
  if (!res.ok)
    throw new Error(`Failed to fetch newsletter analytics: ${res.status}`);
  return res.json();
}

export async function fetchNewsletterAnalyticsSummary() {
  const res = await apiFetch(`${API_BASE}/newsletter-analytics/summary`);
  if (!res.ok)
    throw new Error(`Failed to fetch analytics summary: ${res.status}`);
  return res.json();
}

export async function fetchPollResults(newsletterId) {
  const res = await apiFetch(`${API_BASE}/poll-results/${newsletterId}`);
  if (!res.ok) throw new Error(`Failed to fetch poll results: ${res.status}`);
  return res.json();
}

// ── Email Activity (SendGrid) ─────────────────────────────────

export async function fetchEmailActivity({
  email,
  from,
  to,
  type,
  limit = 50,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (email) params.set("email", email);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (type) params.set("type", type);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const res = await apiFetch(`${API_BASE}/sendgrid-activity?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch email activity: ${res.status}`);
  return res.json();
}

// ── Email Health ──────────────────────────────────────────────

export async function fetchEmailHealthSummary() {
  const res = await apiFetch(`${API_BASE}/email-health/summary`);
  if (!res.ok)
    throw new Error(`Failed to fetch email health summary: ${res.status}`);
  return res.json();
}

export async function fetchEmailHealthContacts({
  filter = "all",
  limit = 100,
  offset = 0,
} = {}) {
  const params = new URLSearchParams({
    filter,
    limit: String(limit),
    offset: String(offset),
  });
  const res = await apiFetch(`${API_BASE}/email-health?${params}`);
  if (!res.ok)
    throw new Error(`Failed to fetch email health contacts: ${res.status}`);
  return res.json();
}

export async function suppressContact(contactId, email, reason) {
  const res = await apiFetch(`${API_BASE}/email-health/suppress`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contactId, email, reason }),
  });
  if (!res.ok) throw new Error(`Failed to suppress contact: ${res.status}`);
  return res.json();
}

export async function clearContactFlags(contactId, email) {
  const res = await apiFetch(`${API_BASE}/email-health/clear`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contactId, email }),
  });
  if (!res.ok) throw new Error(`Failed to clear contact flags: ${res.status}`);
  return res.json();
}

export async function bulkEmailHealthAction(contacts, operation) {
  const res = await apiFetch(`${API_BASE}/email-health/bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contacts, operation }),
  });
  if (!res.ok) throw new Error(`Bulk ${operation} failed: ${res.status}`);
  return res.json();
}

// ── Engagement Scoring ────────────────────────────────────────

export async function triggerRescore(contactId) {
  const params = contactId ? `?contactId=${encodeURIComponent(contactId)}` : "";
  const res = await apiFetch(`${API_BASE}/engagement-score/rescore${params}`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`Rescore failed: ${res.status}`);
  return res.json();
}

// ── Contact Timeline ──────────────────────────────────────────

export async function fetchContactTimeline(
  contactId,
  { limit = 30, offset = 0, types = "all" } = {},
) {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (types && types !== "all") params.set("types", types);
  const res = await apiFetch(
    `${API_BASE}/contacts/${contactId}/timeline?${params}`,
  );
  if (!res.ok) throw new Error(`Failed to fetch timeline: ${res.status}`);
  return res.json();
}

// ── Dashboard Stats ───────────────────────────────────────────

export async function fetchDashboardStats() {
  const res = await apiFetch(`${API_BASE}/dashboard-stats`);
  if (!res.ok)
    throw new Error(`Failed to fetch dashboard stats: ${res.status}`);
  return res.json();
}

export async function fetchLatestNewsletterOpens() {
  const res = await apiFetch(`${API_BASE}/dashboard-newsletter-opens`);
  if (!res.ok)
    throw new Error(`Failed to fetch latest newsletter opens: ${res.status}`);
  return res.json();
}

// ── Pipeline (Stripe) ─────────────────────────────────────────

export async function fetchPipeline({ range = 90 } = {}) {
  const params = new URLSearchParams({ range: String(range) });
  const res = await apiFetch(`${API_BASE}/pipeline?${params}`);
  if (!res.ok) {
    let details = "";
    try {
      const body = await res.json();
      details = body.details || body.error || "";
    } catch {}
    throw new Error(
      `Failed to fetch pipeline: ${res.status}${details ? " — " + details : ""}`,
    );
  }
  return res.json(); // { deals, total, stats, recentPayments }
}

// ── Engagement ────────────────────────────────────────────────

export async function fetchEngagement(
  contactId,
  { limit = 50, type, source } = {},
) {
  const params = new URLSearchParams();
  if (limit) params.set("limit", limit);
  if (type) params.set("type", type);
  if (source) params.set("source", source);
  const res = await apiFetch(
    `${API_BASE}/contacts/${contactId}/engagement?${params}`,
  );
  if (!res.ok) throw new Error(`Failed to fetch engagement: ${res.status}`);
  return res.json(); // { stats, events, total }
}

/**
 * Fetch aggregate engagement stats across all contacts (from GHL custom fields).
 * Returns { totalDelivered, totalOpens, totalClicks, openRate, clickRate, ... }
 */
export async function fetchEngagementStats() {
  const res = await apiFetch(`${API_BASE}/engagement-stats`);
  if (!res.ok)
    throw new Error(`Failed to fetch engagement stats: ${res.status}`);
  return res.json();
}

/**
 * Fetch GHL-sourced engagement data for a contact.
 * Returns { ghlStats, emailHistory, campaigns, totalEmailsSent }
 */
export async function fetchGhlEngagement(contactId) {
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/engagement/ghl`);
  if (!res.ok) throw new Error(`Failed to fetch GHL engagement: ${res.status}`);
  return res.json();
}

// ── Notes ────────────────────────────────────────────────────

export async function fetchNotes(contactId) {
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/notes`);
  if (!res.ok) throw new Error(`Failed to fetch notes: ${res.status}`);
  return res.json(); // { notes: [], total }
}

export async function addNote(contactId, text) {
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/notes`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`Failed to add note: ${res.status}`);
  return res.json();
}

export async function deleteNote(contactId, noteId) {
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/notes`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ noteId }),
  });
  if (!res.ok) throw new Error(`Failed to delete note: ${res.status}`);
}

export async function fetchCompanyTimeline(companyId, { limit = 50, offset = 0, types } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (types) params.set("types", types);
  const res = await apiFetch(
    `${API_BASE}/companies/${encodeURIComponent(companyId)}/timeline?${params}`,
  );
  if (!res.ok) throw new Error(`Failed to fetch company timeline: ${res.status}`);
  return res.json(); // { events, total, hasMore, companyName, contactCount }
}

export async function fetchCompanyEmailSummary(companyId) {
  const res = await apiFetch(
    `${API_BASE}/companies/${encodeURIComponent(companyId)}/email-summary`,
  );
  if (!res.ok) throw new Error(`Failed to fetch company summary: ${res.status}`);
  return res.json();
}

export async function generateCompanyEmailSummary(companyId) {
  const res = await apiFetch(
    `${API_BASE}/companies/${encodeURIComponent(companyId)}/email-summary`,
    { method: "POST" },
  );
  if (!res.ok) throw new Error(`Failed to generate company summary: ${res.status}`);
  return res.json();
}

export async function fetchCompanyNotes(companyId) {
  const res = await apiFetch(
    `${API_BASE}/companies/${encodeURIComponent(companyId)}/notes`,
  );
  if (!res.ok) throw new Error(`Failed to fetch company notes: ${res.status}`);
  return res.json(); // { companyNotes, contactNotes, notes, total }
}

export async function createCompanyNote(companyId, text) {
  const res = await apiFetch(
    `${API_BASE}/companies/${encodeURIComponent(companyId)}/notes`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    },
  );
  if (!res.ok) throw new Error(`Failed to create company note: ${res.status}`);
  return res.json();
}

export async function deleteCompanyNote(companyId, noteId) {
  const res = await apiFetch(
    `${API_BASE}/companies/${encodeURIComponent(companyId)}/notes/${encodeURIComponent(noteId)}`,
    { method: "DELETE" },
  );
  if (!res.ok) throw new Error(`Failed to delete company note: ${res.status}`);
  return res.json();
}

// ── Decision Maker ───────────────────────────────────────────

export async function setDecisionMaker(contactId, email, isDecisionMaker) {
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, isDecisionMaker }),
  });
  if (!res.ok) throw new Error(`Failed to update contact: ${res.status}`);
  return res.json();
}

// ── Zoom Events ──────────────────────────────────────────────

export async function fetchZoomEvents({ all = false, daysAhead } = {}) {
  const params = new URLSearchParams();
  if (all) params.set("all", "true");
  if (daysAhead) params.set("daysAhead", daysAhead);
  const res = await apiFetch(`${API_BASE}/zoom-events?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch events: ${res.status}`);
  return res.json(); // { events: [], total }
}

export async function createZoomEvent(data) {
  const res = await apiFetch(`${API_BASE}/zoom-events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create event: ${res.status}`);
  }
  return res.json();
}

export async function fetchZoomEvent(id) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch event: ${res.status}`);
  return res.json();
}

export async function cancelZoomEvent(id) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to cancel event: ${res.status}`);
  return res.json();
}

export async function fetchZoomRegistrations(eventId) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/registrations`);
  if (!res.ok) throw new Error(`Failed to fetch registrations: ${res.status}`);
  return res.json();
}

export async function registerForZoomEvent(eventId, contact) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/registrations`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contact),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to register: ${res.status}`);
  }
  return res.json();
}

export async function fetchZoomAttendance(eventId) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/attendance`);
  if (!res.ok) throw new Error(`Failed to fetch attendance: ${res.status}`);
  return res.json();
}

export async function fetchZoomRecordings(eventId) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/recordings`);
  if (!res.ok) throw new Error(`Failed to fetch recordings: ${res.status}`);
  return res.json();
}

// ── Zoom Panelists ───────────────────────────────────────────

export async function fetchZoomPanelists(eventId) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/panelists`);
  if (!res.ok) throw new Error(`Failed to fetch panelists: ${res.status}`);
  return res.json();
}

export async function addZoomPanelists(eventId, contacts) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/panelists`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contacts }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to add panelists: ${res.status}`);
  }
  return res.json();
}

export async function removeZoomPanelist(eventId, email) {
  const res = await apiFetch(
    `${API_BASE}/zoom-events/${eventId}/panelists/${encodeURIComponent(email)}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to remove panelist: ${res.status}`);
  }
  return res.json();
}

// ── Zoom Polls ───────────────────────────────────────────────

export async function fetchZoomPolls(eventId) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/polls`);
  if (!res.ok) throw new Error(`Failed to fetch polls: ${res.status}`);
  return res.json();
}

export async function createZoomPoll(eventId, { title, questions }) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/polls`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, questions }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create poll: ${res.status}`);
  }
  return res.json();
}

export async function updateZoomPoll(eventId, pollId, { title, questions }) {
  const res = await apiFetch(
    `${API_BASE}/zoom-events/${eventId}/polls/${pollId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, questions }),
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update poll: ${res.status}`);
  }
  return res.json();
}

export async function deleteZoomPoll(eventId, pollId) {
  const res = await apiFetch(
    `${API_BASE}/zoom-events/${eventId}/polls/${pollId}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete poll: ${res.status}`);
  }
  return res.json();
}

export async function deleteZoomInvitation(eventId, invitationId) {
  const res = await apiFetch(
    `${API_BASE}/zoom-events/${eventId}/invitations/${invitationId}`,
    { method: "DELETE" },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to delete invitation: ${res.status}`);
  }
  return res.json();
}

// ── Zoom Invitations ─────────────────────────────────────────

export async function sendZoomInvitations(
  eventId,
  {
    contacts,
    message,
    sendMethod = "sendgrid",
    senderEmail,
    graphToken,
    emailHtml,
    emailSubject,
    role,
    resend,
  },
) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/invitations`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      contacts,
      message,
      sendMethod,
      senderEmail,
      emailHtml,
      emailSubject,
      ...(role && { role }),
      ...(resend && { resend: true }),
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send invitations: ${res.status}`);
  }
  return res.json();
}

export async function fetchZoomInvitations(eventId) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/invitations`);
  if (!res.ok) throw new Error(`Failed to fetch invitations: ${res.status}`);
  return res.json();
}

// ── Webcast Process Automation ────────────────────────────────

export async function requestTechCheck(
  eventId,
  { panelistEmail, panelistName },
) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/tech-check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ panelistEmail, panelistName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to request tech check: ${res.status}`);
  }
  return res.json();
}

export async function resendZoomInvitations(eventId) {
  const res = await apiFetch(
    `${API_BASE}/zoom-events/${eventId}/invitations/resend`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to resend invitations: ${res.status}`);
  }
  return res.json();
}

export async function sendHostCalendarInvite(eventId, { hosts, sendMethod, senderEmail, graphToken } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/host-invite`, {
    method: "POST",
    headers,
    body: JSON.stringify({ hosts, sendMethod, senderEmail }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send host invite: ${res.status}`);
  }
  return res.json();
}

export async function fetchEventContacts(eventId, segment = "all_invited") {
  const res = await apiFetch(
    `${API_BASE}/zoom-events/${eventId}/contacts?segment=${segment}`,
  );
  if (!res.ok) throw new Error(`Failed to fetch event contacts: ${res.status}`);
  return res.json();
}

export async function updateZoomEvent(eventId, updates) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update event: ${res.status}`);
  }
  return res.json();
}

// ── Audience Lists ──────────────────────────────────────────

export async function fetchAudienceLists() {
  const res = await apiFetch(`${API_BASE}/audience-lists`);
  if (!res.ok) throw new Error(`Failed to fetch audience lists: ${res.status}`);
  return res.json();
}

export async function createAudienceList(list) {
  const res = await apiFetch(`${API_BASE}/audience-lists`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(list),
  });
  if (!res.ok) throw new Error(`Failed to create audience list: ${res.status}`);
  return res.json();
}

export async function fetchAudienceList(id) {
  const res = await apiFetch(`${API_BASE}/audience-lists/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch audience list: ${res.status}`);
  return res.json();
}

export async function updateAudienceList(id, updates) {
  const res = await apiFetch(`${API_BASE}/audience-lists/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Failed to update audience list: ${res.status}`);
  return res.json();
}

export async function deleteAudienceList(id) {
  const res = await apiFetch(`${API_BASE}/audience-lists/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete audience list: ${res.status}`);
}

export async function resolveAudienceList(id, { preview = false } = {}) {
  const params = preview ? "?preview=true" : "";
  const res = await apiFetch(`${API_BASE}/audience-lists/${id}/resolve${params}`);
  if (!res.ok)
    throw new Error(`Failed to resolve audience list: ${res.status}`);
  return res.json();
}

export async function previewAudienceFilters(
  filters,
  excludeSuppressed = true,
) {
  const res = await apiFetch(`${API_BASE}/audience-lists/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filters, excludeSuppressed }),
  });
  if (!res.ok) throw new Error(`Failed to preview filters: ${res.status}`);
  return res.json();
}

// ── Email Delegates ──────────────────────────────────────────

export async function fetchMyAllowedSendAs() {
  const res = await apiFetch(`${API_BASE}/email-delegates?mine=true`);
  if (!res.ok) throw new Error(`Failed to fetch send-as emails: ${res.status}`);
  return res.json();
}

export async function fetchDelegateConfig() {
  const res = await apiFetch(`${API_BASE}/email-delegates`);
  if (!res.ok)
    throw new Error(`Failed to fetch delegate config: ${res.status}`);
  return res.json();
}

export async function updateDelegateConfig(delegates) {
  const res = await apiFetch(`${API_BASE}/email-delegates`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ delegates }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to update delegate config: ${res.status}`,
    );
  }
  return res.json();
}

// ── Email History (O365 Sync) ────────────────────────────────

export async function syncContactEmails(contactId, graphToken, { sinceDate } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/email-sync`, {
    method: "POST",
    headers,
    body: JSON.stringify({ sinceDate }),
  });
  if (!res.ok) throw new Error(`Failed to sync emails: ${res.status}`);
  return res.json();
}

export async function batchSyncEmails(graphToken, { mailboxes, sinceDate, limit, offset } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/email-sync-batch`, {
    method: "POST",
    headers,
    body: JSON.stringify({ mailboxes, sinceDate, limit, offset }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Batch sync failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchContactEmails(contactId, { limit = 30, offset = 0, direction } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (direction) params.set("direction", direction);
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/emails?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch emails: ${res.status}`);
  return res.json();
}

export async function fetchEmailDetail(contactId, emailId) {
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/emails/${emailId}`);
  if (!res.ok) {
    if (res.status === 403) throw new Error("You can only view emails you were party to");
    throw new Error(`Failed to fetch email: ${res.status}`);
  }
  return res.json();
}

export async function summarizeContactEmails(contactId) {
  const res = await apiFetch(`${API_BASE}/contacts/${contactId}/email-summary`, { method: "POST" });
  if (!res.ok) throw new Error(`Failed to summarize emails: ${res.status}`);
  return res.json();
}

// ── User Profiles (page visibility) ──────────────────────────

export async function fetchAppUsers() {
  const res = await apiFetch(`${API_BASE}/app-users`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch app users: ${res.status}`);
  }
  return res.json();
}

export async function fetchUserProfiles() {
  const res = await apiFetch(`${API_BASE}/user-profiles`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to fetch user profiles: ${res.status}`,
    );
  }
  return res.json();
}

export async function fetchMyPageVisibility() {
  const res = await apiFetch(`${API_BASE}/user-profiles?mine=true`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to fetch page visibility: ${res.status}`,
    );
  }
  return res.json();
}

export async function updateUserProfiles(profiles) {
  const res = await apiFetch(`${API_BASE}/user-profiles`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profiles }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to update user profiles: ${res.status}`,
    );
  }
  return res.json();
}

// ── Role Page Access ─────────────────────────────────────────

export async function fetchRolePageAccess() {
  const res = await apiFetch(`${API_BASE}/role-page-access`);
  if (!res.ok) return { pages: {} };
  return res.json();
}

export async function updateRolePageAccess(pages) {
  const res = await apiFetch(`${API_BASE}/role-page-access`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pages }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to update role page access: ${res.status}`,
    );
  }
  return res.json();
}

// ── System Settings ──────────────────────────────────────────

export async function fetchSystemSettings() {
  const res = await apiFetch(`${API_BASE}/system-settings`);
  if (!res.ok) return {};
  return res.json();
}

export async function fetchEmailAuthStatus() {
  const res = await apiFetch(`${API_BASE}/email-auth-status`);
  if (!res.ok)
    throw new Error(`Failed to fetch email auth status: ${res.status}`);
  return res.json();
}

export async function updateSystemSettings(settings) {
  const res = await apiFetch(`${API_BASE}/system-settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(settings),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update settings: ${res.status}`);
  }
  return res.json();
}

// ── Data Cleanup (admin only) ────────────────────────────────

export async function runDataCleanup(action = "preview") {
  const res = await apiFetch(`${API_BASE}/data-cleanup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Data cleanup failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchContactDuplicates() {
  const res = await apiFetch(`${API_BASE}/contacts-duplicates`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Duplicate scan failed: ${res.status}`);
  }
  return res.json();
}

// ── Public Registration (anonymous — no auth) ───────────────

export async function fetchPublicEventInfo(eventId, email, token) {
  const params = new URLSearchParams({ email, token });
  const res = await apiFetch(
    `${API_BASE}/zoom-register-public/${eventId}?${params}`,
  );
  if (!res.ok) throw new Error(`Failed to fetch event info: ${res.status}`);
  return res.json();
}

export async function submitPublicRegistration(
  eventId,
  { email, firstName, lastName, token },
) {
  const res = await apiFetch(`${API_BASE}/zoom-register-public/${eventId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, firstName, lastName, token }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Registration failed: ${res.status}`);
  }
  return res.json();
}

export async function submitPublicSignUp(form) {
  const res = await apiFetch(`${API_BASE}/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(form),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Sign up failed: ${res.status}`);
  }
  return res.json();
}

// ── Media Upload ────────────────────────────────────────────

export async function uploadImage(file) {
  const formData = new FormData();
  formData.append("image", file);
  const res = await apiFetch(`${API_BASE}/media-upload`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Upload failed: ${res.status}`);
  }
  return res.json();
}

// ── Videos (Meetings) ───────────────────────────────────────

export async function fetchMeetings({
  search = "",
  status = "",
  platform = "",
  limit = 50,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (status) params.set("status", status);
  if (platform) params.set("platform", platform);
  params.set("limit", limit);
  params.set("offset", offset);

  const res = await apiFetch(`${API_BASE}/meetings?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch meetings: ${res.status}`);
  return res.json(); // { meetings: [], total: number }
}

export async function fetchMeeting(id) {
  const res = await apiFetch(`${API_BASE}/meetings/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch meeting: ${res.status}`);
  return res.json();
}

export async function createMeeting(data) {
  const res = await apiFetch(`${API_BASE}/meetings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create meeting: ${res.status}`);
  }
  return res.json();
}

export async function updateMeeting(id, updates) {
  const res = await apiFetch(`${API_BASE}/meetings/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Failed to update meeting: ${res.status}`);
  return res.json();
}

export async function deleteMeetingRecord(id) {
  const res = await apiFetch(`${API_BASE}/meetings/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to delete meeting: ${res.status}`);
}

export async function getMeetingUploadToken(filename, mimeType) {
  const res = await apiFetch(`${API_BASE}/meetings/upload`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename, mimeType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to get upload token: ${res.status}`);
  }
  return res.json(); // { sasUrl, blobName, containerName }
}

export async function processMeeting(id) {
  const res = await apiFetch(`${API_BASE}/meetings/${id}/process`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to process meeting: ${res.status}`);
  }
  return res.json();
}

export async function redownloadMeeting(id) {
  const res = await apiFetch(`${API_BASE}/meetings/${id}/redownload`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to re-download recording: ${res.status}`,
    );
  }
  return res.json();
}

export async function fetchMeetingClips(meetingId) {
  const res = await apiFetch(`${API_BASE}/meetings/${meetingId}/clips`);
  if (!res.ok) throw new Error(`Failed to fetch clips: ${res.status}`);
  return res.json();
}

export async function deleteMeetingClip(meetingId, clipId) {
  const res = await apiFetch(`${API_BASE}/meetings/${meetingId}/clips/${clipId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete clip: ${res.status}`);
  return res.json();
}

export async function regenerateMeetingClips(meetingId) {
  const res = await apiFetch(`${API_BASE}/meetings/${meetingId}/clips`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to regenerate clips: ${res.status}`);
  }
  return res.json();
}

// ── Zoom Clips (native Zoom platform clips) ────────────────

export async function fetchZoomClips({
  search,
  from,
  to,
  pageSize,
  pageToken,
} = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (pageSize) params.set("pageSize", String(pageSize));
  if (pageToken) params.set("pageToken", pageToken);
  const qs = params.toString();
  const res = await apiFetch(`${API_BASE}/zoom-clips${qs ? `?${qs}` : ""}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch Zoom clips: ${res.status}`);
  }
  return res.json();
}

export async function fetchZoomClipDownloadUrl(clipId) {
  const res = await apiFetch(
    `${API_BASE}/zoom-clips/${encodeURIComponent(clipId)}?download=1`,
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to get clip download URL: ${res.status}`,
    );
  }
  return res.json();
}

export async function ingestZoomClip(clipId, title, durationSeconds) {
  const res = await apiFetch(`${API_BASE}/zoom-clips-ingest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ clipId, title, durationSeconds }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to ingest Zoom clip: ${res.status}`);
  }
  return res.json();
}

// ── Zoom Cloud Recordings ────────────────────────────────────

export async function fetchZoomCloudRecordings({ from, to, pageSize, pageToken } = {}) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (pageSize) params.set("pageSize", String(pageSize));
  if (pageToken) params.set("pageToken", pageToken);
  const qs = params.toString();
  const res = await apiFetch(`${API_BASE}/zoom-cloud-recordings${qs ? `?${qs}` : ""}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to fetch Zoom recordings: ${res.status}`);
  }
  return res.json();
}

export async function ingestZoomRecording(zoomMeetingId, topic, durationSeconds, type) {
  const res = await apiFetch(`${API_BASE}/zoom-recordings-ingest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ zoomMeetingId, topic, durationSeconds, type }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to ingest Zoom recording: ${res.status}`);
  }
  return res.json();
}

export async function summarizeMeeting(meetingId) {
  const res = await apiFetch(`${API_BASE}/meetings/${meetingId}/summarize`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to summarize meeting: ${res.status}`);
  }
  return res.json();
}

// ── Network Map ──────────────────────────────────────────────

export async function fetchNetworkMap() {
  const res = await apiFetch(`${API_BASE}/network-map`);
  if (!res.ok) throw new Error(`Failed to fetch network map: ${res.status}`);
  return res.json(); // { nodes: [], links: [] }
}

// ── Company Sectors ──────────────────────────────────────────

export async function fetchCompanySectors() {
  const res = await apiFetch(`${API_BASE}/company-sectors`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to fetch company sectors: ${res.status}`,
    );
  }
  return res.json();
}

export async function updateCompanySectors(sectors) {
  const res = await apiFetch(`${API_BASE}/company-sectors`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sectors }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to update company sectors: ${res.status}`,
    );
  }
  return res.json();
}

// ── Domain Aliases ───────────────────────────────────────────

export async function fetchDomainAliases() {
  const res = await apiFetch(`${API_BASE}/domain-aliases`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to fetch domain aliases: ${res.status}`,
    );
  }
  return res.json();
}

export async function updateDomainAliases(merges, splits) {
  const res = await apiFetch(`${API_BASE}/domain-aliases`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ merges, splits }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to update domain aliases: ${res.status}`,
    );
  }
  return res.json();
}

// ── Date formatting ──────────────────────────────────────────

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format an ISO date string as DD-Mon-YY (e.g. "07-Feb-25").
 */
export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}-${mon}-${yy}`;
}

// ── Contact Type Mapping ──────────────────────────────────────

// Default mapping (mirrors api/shared/contact-utils.js)
const DEFAULT_TAG_MAPPING = {
  client: "client",
  "fgf advisory client": "advisory_client",
  "fgf chairman's access client": "chairmans_access",
  "fgf chairman's access": "chairmans_access",
  "partner/collaborator": "partner",
  partner: "partner",
  vip: "vip",
  "active discussion": "active_discussion",
  prospect: "prospect",
  "fgf opportunity": "prospect",
  "interest in ca": "prospect",
  "a list": "prospect",
  "webcast interest": "prospect",
  "still interested": "prospect",
  lead: "lead",
  press: "media",
  "former client": "former_client",
  ffgf: "ffgf",
  "academia np": "non_paying",
  "gov/state np": "non_paying",
  fgf: "team",
  inactive: "inactive",
};

const DEFAULT_TYPE_PRIORITY = [
  "client",
  "advisory_client",
  "chairmans_access",
  "partner",
  "vip",
  "active_discussion",
  "prospect",
  "lead",
  "media",
  "former_client",
  "ffgf",
  "non_paying",
  "team",
  "inactive",
];

// Cached mapping — loaded once, refreshed when Settings page saves
let _cachedMapping = null;
let _cachedPriority = null;

export async function fetchContactTypeMapping() {
  const res = await apiFetch(`${API_BASE}/contact-type-mapping`);
  if (!res.ok)
    return { mapping: DEFAULT_TAG_MAPPING, priority: DEFAULT_TYPE_PRIORITY };
  return res.json();
}

export async function updateContactTypeMapping(mapping, priority) {
  const res = await apiFetch(`${API_BASE}/contact-type-mapping`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mapping, priority }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to update contact type mapping: ${res.status}`,
    );
  }
  const result = await res.json();
  _cachedMapping = result.mapping;
  _cachedPriority = result.priority;
  return result;
}

/**
 * Load the contact type mapping from the API and cache it.
 * Call this once on app startup.
 */
export async function loadContactTypeMapping() {
  try {
    const data = await fetchContactTypeMapping();
    _cachedMapping = data.mapping || DEFAULT_TAG_MAPPING;
    _cachedPriority = data.priority || DEFAULT_TYPE_PRIORITY;
  } catch {
    _cachedMapping = DEFAULT_TAG_MAPPING;
    _cachedPriority = DEFAULT_TYPE_PRIORITY;
  }
}

// ── Contact helpers ───────────────────────────────────────────

/**
 * Derive a display-friendly "type" from GHL tags using the cached mapping.
 * Priority-based: first match in the priority order wins.
 */
export function deriveContactType(tags = [], ghlTags = []) {
  // GHL "inactive" tag always wins — override any CRM type tags
  if (ghlTags.some((t) => t.toLowerCase() === "inactive")) return "inactive";

  const tagMap = _cachedMapping || DEFAULT_TAG_MAPPING;
  const priOrder = _cachedPriority || DEFAULT_TYPE_PRIORITY;
  const allTags = [...tags, ...ghlTags];
  const lower = allTags.map((t) => t.toLowerCase());

  const matchedTypes = new Set();
  for (const tag of lower) {
    if (tagMap[tag]) matchedTypes.add(tagMap[tag]);
  }

  for (const type of priOrder) {
    if (matchedTypes.has(type)) return type;
  }
  return "subscriber";
}

/**
 * Get the canonical (primary) tag string for a given type ID.
 * E.g. "advisory_client" → "fgf advisory client", "prospect" → "prospect"
 * Uses the first tag in the mapping that maps to the given type.
 */
export function getCanonicalTagForType(typeId) {
  const tagMap = _cachedMapping || DEFAULT_TAG_MAPPING;
  for (const [tag, type] of Object.entries(tagMap)) {
    if (type === typeId) return tag;
  }
  return typeId; // fallback: use type ID as tag
}

/**
 * Get all tag strings that are "type tags" (i.e. mapped to a contact type).
 */
export function getAllTypeTags() {
  const tagMap = _cachedMapping || DEFAULT_TAG_MAPPING;
  return Object.keys(tagMap);
}

/**
 * Replace the type-determining tag(s) in a tags array with the canonical tag for a new type.
 * Removes any existing tags that map to a type, then adds the new type's canonical tag.
 * Preserves non-type CRM tags.
 */
export function replaceTypeTag(currentTags = [], newTypeId) {
  const typeTags = new Set(getAllTypeTags());
  const filtered = currentTags.filter((t) => !typeTags.has(t.toLowerCase()));
  const newTag = getCanonicalTagForType(newTypeId);
  return [...filtered, newTag];
}

/**
 * Build a display name from firstName + lastName.
 */
export function displayName(contact) {
  const first = (contact.firstName || "").trim();
  const last = (contact.lastName || "").trim();
  if (first && last) return titleCase(first) + " " + titleCase(last);
  if (first) return titleCase(first);
  if (last) return titleCase(last);
  return contact.email || "Unknown";
}

function titleCase(str) {
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get initials from a contact.
 */
export function initials(contact) {
  const first = (contact.firstName || "")[0] || "";
  const last = (contact.lastName || "")[0] || "";
  return (first + last).toUpperCase() || "?";
}

// ── Book Opportunities ──────────────────────────────────────

export async function fetchBookOpportunities({
  status,
  search,
  limit = 100,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  params.set("limit", limit);
  params.set("offset", offset);
  const res = await apiFetch(`${API_BASE}/book-opportunities?${params}`);
  if (!res.ok)
    throw new Error(`Failed to fetch book opportunities: ${res.status}`);
  return res.json();
}

export async function createBookOpportunity(data) {
  const res = await apiFetch(`${API_BASE}/book-opportunities`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create opportunity: ${res.status}`);
  }
  return res.json();
}

export async function updateBookOpportunity(id, updates) {
  const res = await apiFetch(`${API_BASE}/book-opportunities/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update opportunity: ${res.status}`);
  }
  return res.json();
}

export async function deleteBookOpportunity(id) {
  const res = await apiFetch(`${API_BASE}/book-opportunities/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete opportunity: ${res.status}`);
}

// ── Documents ────────────────────────────────────────────────

export async function fetchDocuments({
  type,
  status,
  companyName,
  contactEmail,
  tag,
  search,
  limit = 50,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (status) params.set("status", status);
  if (companyName) params.set("companyName", companyName);
  if (contactEmail) params.set("contactEmail", contactEmail);
  if (tag) params.set("tag", tag);
  if (search) params.set("search", search);
  params.set("limit", limit);
  params.set("offset", offset);
  const res = await apiFetch(`${API_BASE}/documents?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch documents: ${res.status}`);
  return res.json();
}

export async function fetchDocument(id) {
  const res = await apiFetch(`${API_BASE}/documents/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch document: ${res.status}`);
  return res.json();
}

export async function saveDocument(doc) {
  const method = doc.id ? "PUT" : "POST";
  const url = doc.id
    ? `${API_BASE}/documents/${doc.id}`
    : `${API_BASE}/documents`;
  const res = await apiFetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(doc),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to save document: ${res.status}`);
  }
  return res.json();
}

export async function deleteDoc(id) {
  const res = await apiFetch(`${API_BASE}/documents/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to delete document: ${res.status}`);
}

export async function sendDocument(
  id,
  { sendMethod, senderEmail, recipientEmail, graphToken } = {},
) {
  const headers = { "Content-Type": "application/json" };
  if (graphToken) headers["x-graph-token"] = graphToken;
  const res = await apiFetch(`${API_BASE}/documents/${id}/send`, {
    method: "POST",
    headers,
    body: JSON.stringify({ sendMethod, senderEmail, recipientEmail }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send document: ${res.status}`);
  }
  return res.json();
}

// ── NDA / E-Signature ──────────────────────────────────────

export async function sendNda({ contactId, contactEmail, contactName, companyName, counterSign, counterSignerEmail, counterSignerName }) {
  const res = await apiFetch(`${API_BASE}/nda/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contactId, contactEmail, contactName, companyName, counterSign, counterSignerEmail, counterSignerName }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send NDA: ${res.status}`);
  }
  return res.json();
}

export async function fetchContactNdas(contactId, contactEmail) {
  const params = new URLSearchParams();
  if (contactId) params.set("contactId", contactId);
  if (contactEmail) params.set("contactEmail", contactEmail);
  const res = await apiFetch(`${API_BASE}/nda?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch NDAs: ${res.status}`);
  return res.json();
}

export async function fetchAllNdas({ status, limit, offset } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (limit) params.set("limit", limit);
  if (offset) params.set("offset", offset);
  const res = await apiFetch(`${API_BASE}/nda?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch NDAs: ${res.status}`);
  return res.json();
}

export async function sendDocumentForSignature(id, { recipientEmail, recipientName, counterSign, counterSignerEmail, counterSignerName, fields } = {}) {
  const res = await apiFetch(`${API_BASE}/documents/${id}/sign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ recipientEmail, recipientName, counterSign, counterSignerEmail, counterSignerName, fields }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to send for signature: ${res.status}`);
  }
  return res.json();
}

export async function refreshDocumentStatus(id) {
  const res = await apiFetch(`${API_BASE}/documents/${id}/refresh-status`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to refresh status: ${res.status}`);
  }
  return res.json();
}

export async function getDocumentUploadToken(filename, mimeType) {
  const res = await apiFetch(`${API_BASE}/documents/upload`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename, mimeType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to get upload token: ${res.status}`);
  }
  return res.json();
}

// ── Video Templates ──────────────────────────────────────────

export async function fetchVideoTemplate() {
  const res = await apiFetch(`${API_BASE}/video-template`);
  if (!res.ok) throw new Error(`Failed to fetch video template: ${res.status}`);
  return res.json();
}

export async function getVideoTemplateToken(filename, mimeType) {
  const res = await apiFetch(`${API_BASE}/video-template-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename, mimeType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to get template token: ${res.status}`);
  }
  return res.json();
}

export async function saveVideoTemplate(blobName, filename, mimeType) {
  const res = await apiFetch(`${API_BASE}/video-template-save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ blobName, filename, mimeType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to save template: ${res.status}`);
  }
  return res.json();
}

export async function getVideoUploadToken(eventId, filename, mimeType) {
  const res = await apiFetch(`${API_BASE}/video-upload-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ eventId, filename, mimeType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to get upload token: ${res.status}`);
  }
  return res.json();
}

export async function saveIntroVideoUrl(eventId, introVideoUrl) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/intro-video`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ introVideoUrl }),
  });
  if (!res.ok) throw new Error(`Failed to save intro video: ${res.status}`);
  return res.json();
}

export async function fetchIntroVideoUrl(eventId) {
  const res = await apiFetch(`${API_BASE}/zoom-events/${eventId}/intro-video`);
  if (!res.ok) throw new Error(`Failed to fetch intro video: ${res.status}`);
  return res.json();
}

// -- Audit Logs --

export async function fetchAuditLogs({
  from,
  to,
  user,
  resource,
  action,
  limit = 50,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (user) params.set("user", user);
  if (resource) params.set("resource", resource);
  if (action) params.set("action", action);
  params.set("limit", limit);
  params.set("offset", offset);
  const res = await apiFetch(`${API_BASE}/audit-logs?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch audit logs: ${res.status}`);
  return res.json();
}

// -- Global Search --

export async function globalSearch(q, limit = 5) {
  const params = new URLSearchParams({ q, limit });
  const res = await apiFetch(`${API_BASE}/global-search?${params}`);
  if (!res.ok) throw new Error(`Search failed: ${res.status}`);
  return res.json();
}

// ── Product Catalog ──────────────────────────────────────────

export async function fetchCatalog(docType = "") {
  const params = new URLSearchParams();
  if (docType) params.set("docType", docType);
  const res = await apiFetch(`${API_BASE}/product-catalog?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch catalog: ${res.status}`);
  return res.json(); // { items: [], total }
}

export async function fetchCatalogItem(id) {
  const res = await apiFetch(`${API_BASE}/product-catalog/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch catalog item: ${res.status}`);
  return res.json();
}

export async function createCatalogItem(data) {
  const res = await apiFetch(`${API_BASE}/product-catalog`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to create catalog item: ${res.status}`,
    );
  }
  return res.json();
}

export async function updateCatalogItem(id, data) {
  const res = await apiFetch(`${API_BASE}/product-catalog/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(
      err.error || `Failed to update catalog item: ${res.status}`,
    );
  }
  return res.json();
}

export async function archiveCatalogItem(id, docType) {
  const res = await apiFetch(
    `${API_BASE}/product-catalog/${id}?docType=${docType}`,
    {
      method: "DELETE",
    },
  );
  if (!res.ok) throw new Error(`Failed to archive catalog item: ${res.status}`);
  return res.json();
}

// ── Entitlements ─────────────────────────────────────────────

export async function fetchEntitlements({
  entityType,
  entityId,
  companyName,
  status,
} = {}) {
  const params = new URLSearchParams();
  if (entityType) params.set("entityType", entityType);
  if (entityId) params.set("entityId", entityId);
  if (companyName) params.set("companyName", companyName);
  if (status) params.set("status", status);
  const res = await apiFetch(`${API_BASE}/entitlements?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch entitlements: ${res.status}`);
  return res.json(); // { entitlements: [], total }
}

export async function createEntitlement(data) {
  const res = await apiFetch(`${API_BASE}/entitlements`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create entitlement: ${res.status}`);
  }
  return res.json();
}

export async function updateEntitlement(id, data) {
  const res = await apiFetch(`${API_BASE}/entitlements/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update entitlement: ${res.status}`);
  }
  return res.json();
}

export async function cancelEntitlement(id, entityId) {
  const res = await apiFetch(
    `${API_BASE}/entitlements/${id}?entityId=${encodeURIComponent(entityId)}`,
    {
      method: "DELETE",
    },
  );
  if (!res.ok) throw new Error(`Failed to cancel entitlement: ${res.status}`);
  return res.json();
}

// ── Invoices ─────────────────────────────────────────────────

export async function fetchInvoices({
  status,
  contactId,
  search,
  limit = 50,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (contactId) params.set("contactId", contactId);
  if (search) params.set("search", search);
  params.set("limit", limit);
  params.set("offset", offset);
  const res = await apiFetch(`${API_BASE}/invoices?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch invoices: ${res.status}`);
  return res.json(); // { invoices: [], total }
}

export async function createInvoice(data) {
  const res = await apiFetch(`${API_BASE}/invoices`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create invoice: ${res.status}`);
  }
  return res.json();
}

export async function fetchInvoice(id) {
  const res = await apiFetch(`${API_BASE}/invoices/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch invoice: ${res.status}`);
  return res.json();
}

export async function updateInvoice(id, data) {
  const res = await apiFetch(`${API_BASE}/invoices/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update invoice: ${res.status}`);
  return res.json();
}

export async function sendInvoice(id) {
  const res = await apiFetch(`${API_BASE}/invoices/${id}?action=send`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to send invoice: ${res.status}`);
  return res.json();
}

export async function markInvoicePaid(id) {
  const res = await apiFetch(`${API_BASE}/invoices/${id}?action=mark-paid`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw new Error(`Failed to mark invoice paid: ${res.status}`);
  return res.json();
}

export async function voidInvoice(id) {
  const res = await apiFetch(`${API_BASE}/invoices/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to void invoice: ${res.status}`);
  return res.json();
}

// ── QBO Auth ─────────────────────────────────────────────────

export async function fetchQBOStatus() {
  const res = await apiFetch(`${API_BASE}/qbo-auth?action=status`);
  if (!res.ok) throw new Error(`Failed to fetch QBO status: ${res.status}`);
  return res.json();
}

export async function connectQBO() {
  const res = await apiFetch(`${API_BASE}/qbo-auth?action=connect`);
  if (!res.ok) throw new Error(`Failed to get QBO auth URL: ${res.status}`);
  return res.json(); // { authUrl }
}

export async function disconnectQBO() {
  const res = await apiFetch(`${API_BASE}/qbo-auth`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to disconnect QBO: ${res.status}`);
  return res.json();
}

// ── Enhancements ──────────────────────────────────────────────

export async function fetchEnhancements() {
  const res = await apiFetch(`${API_BASE}/enhancements`);
  if (!res.ok) throw new Error(`Failed to fetch enhancements: ${res.status}`);
  return res.json();
}

export async function createEnhancement(data) {
  const res = await apiFetch(`${API_BASE}/enhancements`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create enhancement: ${res.status}`);
  }
  return res.json();
}

export async function updateEnhancement(id, updates) {
  const res = await apiFetch(`${API_BASE}/enhancements/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update enhancement: ${res.status}`);
  }
  return res.json();
}

export async function deleteEnhancement(id) {
  const res = await apiFetch(`${API_BASE}/enhancements/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete enhancement: ${res.status}`);
}

export async function toggleEnhancementUpvote(id) {
  const res = await apiFetch(`${API_BASE}/enhancements/${id}/upvote`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(`Failed to toggle upvote: ${res.status}`);
  return res.json();
}

export async function addEnhancementComment(id, text) {
  const res = await apiFetch(`${API_BASE}/enhancements/${id}/comment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`Failed to add comment: ${res.status}`);
  return res.json();
}

export async function reorderEnhancements(items) {
  const res = await apiFetch(`${API_BASE}/enhancements/reorder`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) throw new Error(`Failed to reorder: ${res.status}`);
  return res.json();
}

// ── WEB PAGES (CMS) ─────────────────────────────────────────

export async function fetchWebPages({ status = "", search = "", pageType = "" } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (search) params.set("search", search);
  if (pageType) params.set("pageType", pageType);
  const res = await apiFetch(`${API_BASE}/web-pages?${params}`);
  if (!res.ok) throw new Error("Failed to fetch pages");
  return res.json();
}

export async function fetchWebPage(id) {
  const res = await apiFetch(`${API_BASE}/web-pages/${id}`);
  if (!res.ok) throw new Error("Failed to fetch page");
  return res.json();
}

export async function createWebPage(data) {
  const res = await apiFetch(`${API_BASE}/web-pages`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create page: ${res.status}`);
  }
  return res.json();
}

export async function updateWebPage(id, data) {
  const res = await apiFetch(`${API_BASE}/web-pages/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to update page: ${res.status}`);
  }
  return res.json();
}

export async function deleteWebPage(id) {
  const res = await apiFetch(`${API_BASE}/web-pages/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete page");
}

export async function publishStatic() {
  const res = await apiFetch(`${API_BASE}/static-publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Static publish failed: ${res.status}`);
  }
  return res.json();
}

export async function fetchFormSubmissions(pageId, { limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit, offset });
  const res = await apiFetch(`${API_BASE}/form-submissions/${pageId}?${params}`);
  if (!res.ok) throw new Error("Failed to fetch submissions");
  return res.json();
}

// ── Content Blocks ──────────────────────────────────────────

export async function fetchContentBlocks() {
  const res = await apiFetch(`${API_BASE}/content-blocks`);
  if (!res.ok) throw new Error("Failed to fetch content blocks");
  return res.json();
}

export async function saveContentBlock(data) {
  const res = await apiFetch(`${API_BASE}/content-blocks`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to save content block");
  }
  return res.json();
}

// ── Newsletter Archive ──────────────────────────────────────

export async function fetchNewsletterArchive({ limit = 50, offset = 0, includeHidden } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (includeHidden) params.set("includeHidden", "true");
  const res = await apiFetch(`${API_BASE}/newsletter-archive?${params}`);
  if (!res.ok) throw new Error("Failed to fetch newsletter archive");
  return res.json();
}

export async function fetchArchivedNewsletter(id) {
  const res = await apiFetch(`${API_BASE}/newsletter-archive/${id}`);
  if (!res.ok) throw new Error("Failed to fetch archived newsletter");
  return res.json();
}

export async function hideArchivedNewsletter(id) {
  const res = await apiFetch(`${API_BASE}/newsletter-archive/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ hidden: true }),
  });
  if (!res.ok) throw new Error(`Failed to hide archived newsletter: ${res.status}`);
  return res.json();
}

export async function hideInsightsNewsletter(id) {
  const res = await apiFetch(`${API_BASE}/newsletters/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ hidden: true }),
  });
  if (!res.ok) throw new Error(`Failed to hide newsletter: ${res.status}`);
  return res.json();
}

export async function hideGhlCampaign(campaignId) {
  const res = await apiFetch(`${API_BASE}/campaigns/hidden`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ campaignId }),
  });
  if (!res.ok) throw new Error(`Failed to hide campaign: ${res.status}`);
  return res.json();
}

export async function fetchNewsletterViewStats(archiveId) {
  const res = await apiFetch(`${API_BASE}/newsletter-views/${archiveId}`);
  if (!res.ok) throw new Error("Failed to fetch view stats");
  return res.json();
}

export async function fetchNewsletterViewDetails(archiveId, { limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ detail: "true", limit, offset });
  const res = await apiFetch(`${API_BASE}/newsletter-views/${archiveId}?${params}`);
  if (!res.ok) throw new Error("Failed to fetch view details");
  return res.json();
}

// ── Authenticator (TOTP) ─────────────────────────────────────

export async function fetchAuthenticatorEntries() {
  const res = await apiFetch(`${API_BASE}/authenticator`);
  if (!res.ok) throw new Error(`Failed to fetch authenticator entries: ${res.status}`);
  return res.json();
}

export async function createAuthenticatorEntry(data) {
  const res = await apiFetch(`${API_BASE}/authenticator`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create authenticator entry: ${res.status}`);
  }
  return res.json();
}

export async function deleteAuthenticatorEntry(id) {
  const res = await apiFetch(`${API_BASE}/authenticator/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(`Failed to delete authenticator entry: ${res.status}`);
  return res.json();
}

export async function shareAuthenticatorEntry(entryId, shareWith) {
  const res = await apiFetch(`${API_BASE}/authenticator`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "share", entryId, shareWith }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to share entry: ${res.status}`);
  }
  return res.json();
}

// ── Kanban ──────────────────────────────────────────────────

export async function fetchKanbanBoards() {
  const res = await apiFetch(`${API_BASE}/kanban/boards`);
  if (!res.ok) throw new Error("Failed to fetch boards");
  return res.json();
}

export async function createKanbanBoard(board) {
  const res = await apiFetch(`${API_BASE}/kanban/boards`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(board),
  });
  if (!res.ok) throw new Error("Failed to create board");
  return res.json();
}

export async function updateKanbanBoard(boardId, data) {
  const res = await apiFetch(`${API_BASE}/kanban/boards/${boardId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update board");
  return res.json();
}

export async function deleteKanbanBoard(boardId) {
  const res = await apiFetch(`${API_BASE}/kanban/boards/${boardId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete board");
  return res.json();
}

export async function fetchKanbanItems(boardId, filters = {}) {
  const params = new URLSearchParams();
  if (filters.type) params.set("type", filters.type);
  if (filters.columnId) params.set("columnId", filters.columnId);
  if (filters.dueBefore) params.set("dueBefore", filters.dueBefore);
  if (filters.dueAfter) params.set("dueAfter", filters.dueAfter);
  const qs = params.toString();
  const res = await apiFetch(`${API_BASE}/kanban/boards/${boardId}/items${qs ? `?${qs}` : ""}`);
  if (!res.ok) throw new Error("Failed to fetch items");
  return res.json();
}

export async function createKanbanItem(boardId, item) {
  const res = await apiFetch(`${API_BASE}/kanban/boards/${boardId}/items`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(item),
  });
  if (!res.ok) throw new Error("Failed to create item");
  return res.json();
}

export async function updateKanbanItem(boardId, itemId, data) {
  const res = await apiFetch(`${API_BASE}/kanban/boards/${boardId}/items/${itemId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to update item");
  return res.json();
}

export async function deleteKanbanItem(boardId, itemId) {
  const res = await apiFetch(`${API_BASE}/kanban/boards/${boardId}/items/${itemId}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete item");
  return res.json();
}

// ── Video Library ─────────────────────────────────────────────

export async function fetchVideoLibrary({
  search = "",
  source = "",
  limit = 50,
  offset = 0,
} = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (source) params.set("source", source);
  params.set("limit", limit);
  params.set("offset", offset);
  const res = await apiFetch(`${API_BASE}/video-library?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch videos: ${res.status}`);
  return res.json(); // { items: [], total: number }
}

export async function fetchVideoLibraryItem(id) {
  const res = await apiFetch(`${API_BASE}/video-library/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch video: ${res.status}`);
  return res.json();
}

export async function createVideoLibraryItem(data) {
  const res = await apiFetch(`${API_BASE}/video-library`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to create video: ${res.status}`);
  }
  return res.json();
}

export async function updateVideoLibraryItem(id, updates) {
  const res = await apiFetch(`${API_BASE}/video-library/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Failed to update video: ${res.status}`);
  return res.json();
}

export async function deleteVideoLibraryItem(id) {
  const res = await apiFetch(`${API_BASE}/video-library/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to delete video: ${res.status}`);
  return res.json();
}

export async function getVideoLibraryUploadToken(filename, mimeType) {
  const res = await apiFetch(`${API_BASE}/video-library/upload`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ filename, mimeType }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to get upload token");
  }
  return res.json(); // { sasUrl, blobName, containerName }
}

export async function processVideoLibraryItem(id) {
  const res = await apiFetch(`${API_BASE}/video-library/${id}/process`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Failed to start processing");
  }
  return res.json();
}

// ── Clips (aggregated) ───────────────────────────────────────

export async function fetchClips({
  search = "",
  source = "",
  category = "",
  limit = 50,
  offset = 0,
  showArchived = false,
} = {}) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (source) params.set("source", source);
  if (category) params.set("category", category);
  if (showArchived) params.set("showArchived", "true");
  params.set("limit", limit);
  params.set("offset", offset);
  const res = await apiFetch(`${API_BASE}/clips?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch clips: ${res.status}`);
  return res.json(); // { clips: [], total: number }
}

export async function updateClip(videoId, clipId, updates) {
  const res = await apiFetch(`${API_BASE}/clips/${videoId}/${clipId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error(`Failed to update clip: ${res.status}`);
  return res.json();
}

export async function bulkUpdateClips(operations) {
  const res = await apiFetch(`${API_BASE}/clips-bulk-update`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operations }),
  });
  if (!res.ok) throw new Error(`Failed to bulk update clips: ${res.status}`);
  return res.json();
}

// ── LinkedIn ──────────────────────────────────────────────────

export async function getLinkedInStatus() {
  const res = await apiFetch(`${API_BASE}/linkedin-auth?action=status`);
  if (!res.ok) throw new Error(`Failed to get LinkedIn status: ${res.status}`);
  return res.json();
}

export async function connectLinkedIn() {
  const res = await apiFetch(`${API_BASE}/linkedin-auth?action=connect`);
  if (!res.ok) throw new Error(`Failed to get LinkedIn auth URL: ${res.status}`);
  return res.json(); // { authUrl }
}

export async function disconnectLinkedIn() {
  const res = await apiFetch(`${API_BASE}/linkedin-auth`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Failed to disconnect LinkedIn: ${res.status}`);
  return res.json();
}

export async function postToLinkedIn({ text, videoId, targets }) {
  const res = await apiFetch(`${API_BASE}/linkedin-post`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, videoId, targets }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `LinkedIn post failed: ${res.status}`);
  }
  return res.json(); // { results: [{ target, success, data? }] }
}

// ── Content Studio ──────────────────────────────────────────

export async function fetchContentDrafts({ limit = 50, offset = 0, status, channelStatus, search } = {}) {
  const params = new URLSearchParams({ limit, offset });
  if (status) params.set("status", status);
  if (channelStatus) params.set("channelStatus", channelStatus);
  if (search) params.set("search", search);
  const res = await apiFetch(`${API_BASE}/content-drafts?${params}`);
  if (!res.ok) throw new Error("Failed to fetch drafts");
  return res.json();
}

export async function fetchContentDraft(id) {
  const res = await apiFetch(`${API_BASE}/content-drafts/${id}`);
  if (!res.ok) throw new Error("Failed to fetch draft");
  return res.json();
}

export async function createContentDraft(data) {
  const res = await apiFetch(`${API_BASE}/content-drafts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to create draft");
  return res.json();
}

export async function updateContentDraft(id, updates) {
  const res = await apiFetch(`${API_BASE}/content-drafts/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
  if (!res.ok) throw new Error("Failed to update draft");
  return res.json();
}

export async function deleteContentDraft(id) {
  const res = await apiFetch(`${API_BASE}/content-drafts/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to delete draft");
}

export async function approveContentDraft(id, comment) {
  const res = await apiFetch(`${API_BASE}/content-drafts/${id}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  });
  if (!res.ok) throw new Error("Failed to approve draft");
  return res.json();
}

export async function rejectContentDraft(id, comment) {
  const res = await apiFetch(`${API_BASE}/content-drafts/${id}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comment }),
  });
  if (!res.ok) throw new Error("Failed to reject draft");
  return res.json();
}

// ── Content Studio Publishing ───────────────────────────

export async function publishContentDraft(id, channels) {
  const res = await apiFetch(`${API_BASE}/content-drafts/${id}/publish`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ channels }),
  });
  if (!res.ok) throw new Error("Failed to publish draft");
  return res.json();
}

// ── YouTube Auth ────────────────────────────────────────

export async function getYouTubeStatus() {
  const res = await apiFetch(`${API_BASE}/youtube-auth?action=status`);
  if (!res.ok) return { connected: false };
  return res.json();
}

export async function connectYouTube() {
  const res = await apiFetch(`${API_BASE}/youtube-auth?action=connect`);
  if (!res.ok) throw new Error("Failed to start YouTube connection");
  return res.json();
}

export async function disconnectYouTube() {
  const res = await apiFetch(`${API_BASE}/youtube-auth`, { method: "DELETE" });
  if (!res.ok) throw new Error("Failed to disconnect YouTube");
  return res.json();
}

export async function getYouTubePlaylists() {
  const res = await apiFetch(`${API_BASE}/youtube-auth?action=playlists`);
  if (!res.ok) return { playlists: [] };
  return res.json();
}
